<template>
  <timeseries-chart
    :title="title"
    :y-axis="{ titleText: yAxisLabel }"
    :grouped="grouped"
    :stacked="stackedComputed"
    :graph-type-selector="graphTypeSelector"
    :seasonal="seasonal"
    :seasonal-initial-value="seasonalInitialValue"
    :moving-avg="movingAvg"
    :moving-avg-initial-value="movingAvgInitialValue"
    :export-types="exportTypes"
    :export-loading="exportLoading"
    @export="$emit('export', $event)"
    :series="groupedSeries"
    :dates="formattedDates"
    :values="formattedValues">
    <template #top-controls>
      <div class="extra-controls">
        <div class="buttons-select">
          <buttons-select :choices="mode.choices" v-model="mode.value"/>
        </div>

        <b-form-group
          v-if="grouped"
          class="select_group"
          label=""
          :label-cols="'auto'">
          <b-form-select
            :plain="false"
            v-model="group.value"
            :options="group.choices"
            :disabled="!!getActiveRequestsCount"
            @change="$emit('update', { chartId, group: $event })">
          </b-form-select>
        </b-form-group>
      </div>
    </template>
  </timeseries-chart>
</template>

<script>
import getNested from 'lodash-es/get';
import isEmpty from 'lodash-es/isEmpty';
import { mapGetters } from 'vuex';
import TimeseriesChart from '@/components/widgets/TimeseriesChart';
import ButtonsSelect from '@/components/widgets/ButtonsSelect';

export default {
  name: 'congestion-timeseries-chart',

  components: {
    ButtonsSelect,
    TimeseriesChart,
  },

  props: {
    title: {
      type: String,
      default: 'No title specified',
    },

    chartId: {
      type: String,
      default: 'no-id-specified',
    },

    stats: {
      type: Array,
      default: () => [],
    },

    graphTypeSelector: {
      type: Boolean,
      default: false,
    },

    groupValue: {
      type: String,
      default: null,
    },

    grouped: {
      type: Boolean,
      default: false,
    },

    seasonal: {
      type: Boolean,
      default: true,
    },

    seasonalInitialValue: {
      type: Boolean,
      default: false,
    },

    movingAvg: {
      type: Boolean,
      default: true,
    },

    movingAvgInitialValue: {
      type: Number,
      default: null,
    },

    exportTypes: {
      type: Array,
      default: () => null,
    },

    exportLoading: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      mode: {
        value: 'count',
        choices: [
          {value: 'count', text: 'Count', yAxisLabel: '# of vessels'},
          {value: 'dwt', text: 'DWT', yAxisLabel: 'Tons'},
          {
            value: 'avgWaitingDays',
            text: 'Waiting time (avg)',
            yAxisLabel: 'Days',
          },
          {
            value: 'medianWaitingDays',
            text: 'Waiting time (median)',
            yAxisLabel: 'Days',
          },
        ],
      },
      group: {
        value: this.groupValue,
        choices: [
          {text: 'No grouping', value: null},
          {text: 'Commodity group', value: 'commodity_group'},
          {text: 'Commodity value', value: 'commodity'},
          {text: 'Segment', value: 'segment'},
          {text: 'Sub Segment', value: 'sub_segment'},
          {text: 'Last Load Country', value: 'last_load_country_code'},
          {text: 'Current Country', value: 'country_code'},
          {text: 'Current Region', value: 'region'},
          // { text: "Current Port", value: "port_name" }
        ],
      },
    };
  },

  methods: {
    applyFilter(groupByValue) {
      this.$emit('update', groupByValue);
    },

    convertRowsToTimeseries(rows) {
      return rows.map(item => {
        return {
          date: getNested(item, 'date', '2001-01-01'),
          value: getNested(item, this.mode.value, -1),
        };
      });
    },

    getYAxisLabel(value) {
      const choiceItem = this.mode.choices.find(item => item.value === value);
      return getNested(choiceItem, 'yAxisLabel', '--');
    },
  },

  computed: {
    ...mapGetters('api', ['getActiveRequestsCount']),

    stackedComputed() {
      // if values type is count/dwt, graph is stacked, otherwise - not
      return ['count', 'dwt'].includes(this.mode.value);
    },

    groupedSeries() {
      const isOnlyGroup = this.stats.length === 1;

      if(this.grouped) {
        return this.stats.map(section => ({
          name: isOnlyGroup
            ? this.getYAxisLabel(this.mode.value)
            : section.group,
          data: section.rows.map(item => [
            item.date,
            getNested(item, this.mode.value, -1),
          ]),
        }));
      } else {
        return [];
      }
    },

    formattedItems() {
      if(isEmpty(this.stats)) {
        return [];
      }

      const defaultSection =
        this.stats.find(section => section.group === 'default') || {};
      return getNested(defaultSection, 'rows', []).map(item => {
        return {
          date: getNested(item, 'date', '2001-01-01'),
          value: getNested(item, this.mode.value, -1),
        };
      });
    },

    formattedDates() {
      if(this.grouped) {
        return null;
      } else {
        return this.formattedItems.map(item => item.date);
      }
    },

    formattedValues() {
      if(this.grouped) {
        return null;
      } else {
        return this.formattedItems.map(item => item.value);
      }
    },

    yAxisLabel() {
      return this.getYAxisLabel(this.mode.value);
    },
  },
};
</script>

<style scoped>
.buttons-select >>> .choice-buttons {
  margin: 0;
}
</style>

<style scoped lang="scss">
.extra-controls {
  display: flex;
  justify-content: space-between;
  align-items: center;
  align-content: flex-end;
  flex-wrap: nowrap;
  margin-top: 10px;

  .buttons-select {
    display: flex;
    justify-content: flex-end;
    margin-top: 4px;
  }

  .select_group {
    margin: 0 0 0 auto;
  }
}
</style>
